import { useBasicGiftCardContext } from '@/src/common/contexts/BasicGiftCardContext'
import HTMLFlipBook from 'react-pageflip'
import { forwardRef, ReactNode, useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { styled } from '@/src/stitches.config'
import { formatAmountForDisplay } from '@/src/common/utilities/currencyFormatter'
import Button from '@/src/common/components/marketing/buttons/Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBookOpen } from '@fortawesome/free-solid-svg-icons/faBookOpen'
import { faFileImage } from '@fortawesome/free-regular-svg-icons/faFileImage'
import Card from '@/src/common/components/marketing/cards/Card'
import { CARRIER_FONT_HANDWRITTEN } from '@/src/modules/build-a-card/constants'
import { usePlasticGiftQuantityContext } from '@/src/common/contexts/PlasticGiftQuantityContext'

const Wrapper = styled('div', {
    pt: '7.5%',
    '.greetingCardOpen': {
        transform: 'translateX(0) !important',
        transition: 'transform 0.5s',
    },
    '.active-button-gradient': {
        color: '$primary !important',
    },
    '.carrierInfoPageMessage': {
        fontStyle: 'italic',
        paddingLeft: 0,
        mt: '0',
    },
})

const PageWithShadow = styled('div', {
    boxShadow: `0 0.3px 0.9px rgba(0, 0, 0, 0.037), 
            0 0.8px 2.2px rgba(0, 0, 0, 0.053), 
            0 1.5px 4.1px rgba(0, 0, 0, 0.065), 
            0 2.7px 7.4px rgba(0, 0, 0, 0.077), 
            0 5px 13.8px rgba(0, 0, 0, 0.093), 
            0 12px 33px rgba(0, 0, 0, 0.13)`,
})

const GreetingCardPageContainer = styled(PageWithShadow, {
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundColor: 'white',
    padding: '5%',
    overflowY: 'hidden',
    variants: {
        backgroundImage: {
            left: {
                backgroundImage:
                    'url("https://res.cloudinary.com/gift-card-granny/image/upload/f_auto,w_284/v1635259997/GCG/assets/greeting-card-panel-left_d1zvcn")',
            },
            right: {
                backgroundImage: `url('https://res.cloudinary.com/gift-card-granny/image/upload/f_auto,w_284/v1635259998/GCG/assets/greeting-card-panel-right_gsjbov')`,
            },
        },
    },
    defaultVariant: {
        backgroundImage: 'left',
    },
})

const StyledFlipBook = styled(HTMLFlipBook, {
    marginBottom: '2rem',
    transform: 'translateX(-42%) !important',
    transition: 'transform 0.5s',
})
const GreetingCardImage = styled(Image, {
    height: '100%',
    width: '100%',
    opacity: 1,
    transition: 'opacity 300ms',
})
const GreetingCardImageContainer = styled('div', {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '95%',
    my: '9%',
    '& p': {
        fontSize: 6,
        mb: 2,
        '@media (min-width: 430px)': {
            fontSize: 9,
        },
    },
    '.dateFinal': {
        display: 'none',
    },
})
const ButtonsWrapper = styled('div', {
    display: 'flex',
    justifyContent: 'center',
})

const CarrierInfoPageText = styled('p', {
    overflowWrap: 'break-word',
    variants: {
        variant: {
            default: {
                fontSize: '2.1vw',
                lineHeight: '1.5',
                paddingLeft: '7%',
                whiteSpace: 'pre-line',
                '@media (min-width: 900px)': {
                    fontSize: 10,
                },
                '@media (min-width: 1024px)': {
                    fontSize: 11,
                },
                '@media (min-width: 1150px)': {
                    fontSize: 13,
                },
                '@media (min-width: 1250px)': {
                    fontSize: 15,
                },
            },
            handwrytten: {
                fontSize: '5.7vw',
                lineHeight: 0.8,
                whiteSpace: 'pre-line',
                color: '#173fa6', // handwritten text is blue in real-life
                '@md': {
                    fontSize: '2.6vw',
                },
                '@media (min-width: 1440px)': {
                    fontSize: 40,
                },
            },
        },
    },
    defaultVariants: {
        variant: 'default',
    },
})

const StyledFlipButton = styled(Button, {
    gap: 5,
    color: '$mediumGray !important',
    br: '0px !important',
    marginRight: '0px !important',
    marginBottom: '0px !important',
    fontSize: '.85rem !important',
    '& svg': {
        fontSize: '1rem !important',
    },
    variants: {
        borderRadius: {
            default: {
                br: '0px !important',
            },
            leftButton: {
                borderTopLeftRadius: '5px !important',
                borderBottomLeftRadius: '5px !important',
            },
            rightButton: {
                borderTopRightRadius: '5px !important',
                borderBottomRightRadius: '5px !important',
            },
        },
    },
    defaultVariant: {
        borderRadius: 'default',
    },
})

export const PageCover = forwardRef(
    (props: { cardImage: string }, ref: any) => {
        return (
            <PageWithShadow ref={ref} data-density="hard">
                {props.cardImage && (
                    <GreetingCardImage
                        src={props.cardImage}
                        width={640}
                        height={896}
                        loading="lazy"
                        title={`Greeting card cover design`}
                        alt={`Greeting card cover design`}
                        data-testid={'greeting-card-preview'}
                    />
                )}
            </PageWithShadow>
        )
    }
)
PageCover.displayName = 'PageCover'

export const Page = forwardRef(
    (
        props: { children: ReactNode; imageVariant: 'left' | 'right' },
        ref: any
    ) => {
        return (
            <GreetingCardPageContainer
                ref={ref}
                backgroundImage={props?.imageVariant}
            >
                {props?.children}
            </GreetingCardPageContainer>
        )
    }
)
Page.displayName = 'Page'

interface IGreetingCard {
    displayPageButtons: boolean
    defaultPage?: number
}

const exampleHandwrittenText = `Happy Holidays Jane!!
Wishing you a very merry Christmas, Kwanza, and Hanukkah to you and your loved ones.

Happy New Year,
- John`

const GreetingCardWithFlip = ({
    displayPageButtons,
    defaultPage = 0,
}: IGreetingCard) => {
    const {
        greetingCards,
        greetingCardUuid,
        nameOnCard,
        messageOnCard,
        denomination,
        designImage,
        fontSelection,
        handwrittenFontClassName,
    } = useBasicGiftCardContext()
    const { giftState } = usePlasticGiftQuantityContext()
    const { listOfRecipients } = giftState

    const greetingCardRef = useRef<any>()
    const isHandwritten: boolean = fontSelection === CARRIER_FONT_HANDWRITTEN
    const recipientMessage: string = listOfRecipients[0].recipientMessage
    const [activePage, setActivePage] = useState(defaultPage)

    useEffect(() => {
        const showInside = defaultPage === 1
        showInside ? nextButtonClick() : prevButtonClick()
    }, [defaultPage])

    const greetingCardImage =
        greetingCards?.filter((design) => design.uuid === greetingCardUuid)[0]
            ?.greeting_card_preview_url ??
        greetingCards[0].greeting_card_preview_url

    const nextButtonClick = () => {
        setActivePage(1)
        greetingCardRef?.current?.pageFlip()?.flipNext()
    }

    const prevButtonClick = () => {
        setActivePage(0)
        greetingCardRef?.current?.pageFlip()?.flipPrev()
    }

    return (
        <Wrapper>
            <StyledFlipBook
                ref={greetingCardRef}
                width={550}
                height={733}
                minWidth={100}
                minHeight={100}
                maxWidth={550}
                maxHeight={733}
                size="stretch"
                drawShadow={false}
                showCover={true}
                autoSize={true}
                mobileScrollSupport={true}
                useMouseEvents={false}
                usePortrait={false}
                className={activePage === 1 ? 'greetingCardOpen' : ''}
                startPage={defaultPage}
                style={{}}
                flippingTime={1000}
                startZIndex={0}
                maxShadowOpacity={1}
                swipeDistance={30}
                clickEventForward={true}
                disableFlipByClick={false}
                showPageCorners={true}
            >
                <PageCover
                    ref={greetingCardRef}
                    cardImage={greetingCardImage ?? ''}
                />
                <Page imageVariant={'left'}>
                    <CarrierInfoPageText
                        className={`carrierInfoPageMessage ${
                            isHandwritten && handwrittenFontClassName
                        }`}
                        data-testid="card-message"
                        variant={isHandwritten ? 'handwrytten' : 'default'}
                    >
                        {recipientMessage.length
                            ? recipientMessage
                            : exampleHandwrittenText}
                    </CarrierInfoPageText>
                </Page>
                <Page imageVariant={'right'}>
                    {greetingCardImage && (
                        <GreetingCardImageContainer>
                            <Card
                                size={'md'}
                                image={designImage}
                                isExtended={false}
                                overlayType={'full'}
                                showCustomFields={true}
                                showLoader={!designImage}
                                name={nameOnCard}
                                message={messageOnCard}
                                borderRadius={8}
                            />
                        </GreetingCardImageContainer>
                    )}

                    <CarrierInfoPageText
                        data-testid="card-value"
                        style={{ marginTop: 0 }}
                    >
                        <span className={'bold'}>Value: </span>
                        {formatAmountForDisplay(Number(denomination), 'USD')}
                    </CarrierInfoPageText>
                </Page>
            </StyledFlipBook>
            {displayPageButtons && (
                <ButtonsWrapper>
                    <div style={{ display: 'flex' }}>
                        <StyledFlipButton
                            label={'front'}
                            variant={'graySquareButtonWithGradient'}
                            textTransform={'uppercase'}
                            className={
                                activePage === 0 ? 'active-button-gradient' : ''
                            }
                            onClick={prevButtonClick}
                            aria-label="View the front cover of your greeting card"
                            borderRadius={'leftButton'}
                        >
                            <FontAwesomeIcon icon={faFileImage} />
                        </StyledFlipButton>
                        <StyledFlipButton
                            label={'inside'}
                            textTransform={'uppercase'}
                            variant={'graySquareButtonWithGradient'}
                            className={
                                activePage === 1 ? 'active-button-gradient' : ''
                            }
                            onClick={nextButtonClick}
                            aria-label="View the inside of your greeting card"
                            borderRadius={'rightButton'}
                        >
                            <FontAwesomeIcon icon={faBookOpen} />
                        </StyledFlipButton>
                    </div>
                </ButtonsWrapper>
            )}
        </Wrapper>
    )
}

export default GreetingCardWithFlip
